import { tick } from 'svelte';
import { scrollToNode } from 'components/move-list-shared/scroll-to-node';
import { getMoveNodeEl } from './get-move-node-el';
import type { Store } from '../../store/types';
import { isMoveListScrolledUp } from './is-move-list-scrolled-up';

/**
 * Handles DOM updates related to the selected node changing.
 */
export async function handleSelectedNodeChangeDOMUpdates(
  store: Store,
  options: {
    disableScrollIfScrolledUp?: boolean;
  } = {},
): Promise<void> {
  // Why: With our current data model, manually doing these DOM updates is more performant than using Svelte's reactivity.

  const moveListEl = store.moveListEl.get();
  const scrollContainer = store.scrollContainer.get();

  // need to do this before the dom is updated with any new nodes
  const moveListIsScrolledUp = isMoveListScrolledUp(scrollContainer ?? moveListEl);

  // wait for svelte to update dom first
  await tick();

  const selectedNodeKeys = store.selectedNodeKeys.get();
  const previousNodeEl = store.selectedNodeEl.get();
  previousNodeEl?.classList.remove('selected');

  if (!selectedNodeKeys || !moveListEl) {
    store.selectedNodeEl.set(null);
    return;
  }

  const nodeWrapperEl = getMoveNodeEl(
    moveListEl,
    selectedNodeKeys.ids.line,
    selectedNodeKeys.ids.move,
  ) as HTMLElement | null;

  const nodeEl = nodeWrapperEl?.querySelector('.node-highlight-content') as HTMLElement | undefined;

  if (!nodeEl || !nodeWrapperEl) {
    store.selectedNodeEl.set(null);
    return;
  }

  const finishedInitialLoad = store.finishedInitialLoad.get();
  if (!options.disableScrollIfScrolledUp || !moveListIsScrolledUp) {
    scrollToMoveNode(nodeWrapperEl, moveListEl, scrollContainer, {
      scrollToTop: !finishedInitialLoad,
    });
  }

  if (!finishedInitialLoad) {
    store.finishedInitialLoad.set(true);
  }

  nodeEl.classList.add('selected');
  store.selectedNodeEl.set(nodeEl);
}

/**
 * Scrolls to a move node element in the move list
 */
function scrollToMoveNode(
  nodeEl: HTMLElement | null,
  moveListEl: HTMLElement,
  scrollContainer: HTMLElement | undefined,
  options: { scrollToTop?: boolean } = {},
): void {
  if (!nodeEl) {
    return;
  }

  scrollToNode({
    nodeEl,
    moveListEl,
    containerEl: scrollContainer,
    position: options.scrollToTop ? 'top' : 'nearest-edge',
    onlyIfOutside: !options.scrollToTop,
  });
}
